import moment from "moment";
import * as R from "ramda";
import CryptoJS from "crypto-js";
import TripleDES from "crypto-js/tripledes";
import { Fraction } from "fractional";
import countries from "./countries.json";
import Croppie from "croppie";

export const getCountries = (lang) =>
  countries.map((country) => ({
    value: country.code,
    countryName:
      country.translate && country.translate[lang]
        ? country.translate[lang]
        : country.defaultName || country.code,
  }));

export const getStates = (selectedCountry, lang) => {
  const country = R.find(R.propEq("code", selectedCountry), countries);
  const states = country?.states || [];
  return states.map((state) => ({
    value: state.code,
    stateName:
      state.translate && state.translate[lang]
        ? state.translate[lang]
        : state.defaultName || state.code,
  }));
};

export const now = () => moment().format("x") * 1;

// Converts from degrees to radians.
export const radians = (degrees) => (degrees * Math.PI) / 180;

// Converts from radians to degrees.
export const degrees = (radians) => (radians * 180) / Math.PI;

export const lpad = (value, padding) => {
  let zeroes = new Array(padding + 1).join("0");
  return (zeroes + value).slice(-padding);
};

export const getLocalStorageInfo = (databaseName) => {
  let localStorageInfo = window
    ? {
        spectre_country: window.localStorage.getItem("spectre_country"),
        spectre_language: window.localStorage.getItem("spectre_language"),
        spectre_lastcustomerInfo: window.localStorage.getItem(
          "spectre_lastcustomerInfo"
        ),
        spectre_user_settings: window.localStorage.getItem(
          "spectre_user_settings"
        ),
        spectre_userSession: window.localStorage.getItem("spectre_userSession"),
        spectre_userSessionId: window.localStorage.getItem(
          "spectre_userSessionId"
        ),
        spectre_version: window.localStorage.getItem("spectre_version"),
        databaseName,
      }
    : { databaseName };

  return JSON.stringify(localStorageInfo);
};

export const formatPhoneNumber = (phoneNumberString) => {
  let cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    let intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return phoneNumberString;
};

export const setLocalStorageInfo = (localStorageInfoString) => {
  const localStorageInfo = JSON.parse(localStorageInfoString);
  if (window) {
    window.localStorage.setItem(
      "spectre_country",
      localStorageInfo.spectre_country
    );
    window.localStorage.setItem(
      "spectre_language",
      localStorageInfo.spectre_language
    );
    window.localStorage.setItem(
      "spectre_lastcustomerInfo",
      localStorageInfo.spectre_lastcustomerInfo
    );
    window.localStorage.setItem(
      "spectre_user_settings",
      localStorageInfo.spectre_user_settings
    );
    window.localStorage.setItem(
      "spectre_userSession",
      localStorageInfo.spectre_userSession
    );
    window.localStorage.setItem(
      "spectre_userSessionId",
      localStorageInfo.spectre_userSessionId
    );
    window.localStorage.setItem(
      "spectre_version",
      localStorageInfo.spectre_version
    );
  }

  return localStorageInfo;
};

export const _toBlob = (bString) => {
  let l, d, array;
  d = bString;
  l = d.length;
  array = new Uint8Array(l);
  for (let i = 0; i < l; i++) {
    array[i] = d.charCodeAt(i);
  }
  return new Blob([array], { type: "application/octet-stream" });
};

export const b64toBlob = (b64Data, contentType, sliceSize) => {
  contentType = contentType || "";
  sliceSize = sliceSize || 512;

  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    let slice = byteCharacters.slice(offset, offset + sliceSize);

    let byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    let byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

export const encryptSession = (userSession, dataId) => {
  const encryptedSession = TripleDES.encrypt(
    JSON.stringify(userSession),
    dataId
  );

  window.localStorage.setItem("spectre_userSession", encryptedSession);
  window.localStorage.setItem("spectre_userSessionId", dataId);
};

export const deleteSession = () => {
  window.localStorage.removeItem("spectre_userSession");
  window.localStorage.removeItem("spectre_userSessionId");
};

export const decryptSession = () => {
  const encryptedSession = window.localStorage.getItem("spectre_userSession");
  const sessionId = window.localStorage.getItem("spectre_userSessionId");

  if (encryptedSession) {
    return JSON.parse(
      TripleDES.decrypt(encryptedSession, sessionId).toString(CryptoJS.enc.Utf8)
    );
  } else {
    return;
  }
};

export const decryptSessionCLI = (encryptedSession, sessionId) => {
  if (encryptedSession) {
    return JSON.parse(
      TripleDES.decrypt(encryptedSession, sessionId).toString(CryptoJS.enc.Utf8)
    );
  } else {
    return;
  }
};

export const getDatabaseName = (name, version) => `v${version}_${name}`;

export const specSheetDecimalValues = (idx_data, fraction) => {
  const result = R.find(R.propEq("fraction", fraction))(idx_data);

  if (result) {
    return result.decimal;
  } else {
    return 0;
  }
};

export const calculateFlexPitch = (
  f1_span_full,
  thm_flex,
  idx_data_span_measurement
) => {
  const initialFraction = {
    65: "2",
    75: "2",
    90: "2 1/8",
    105: "2 1/4",
    120: "2 3/8",
    135: "2 1/2",
  };

  const spanDecimal = specSheetDecimalValues(
    idx_data_span_measurement,
    f1_span_full
  );
  let spanFraction = new Fraction(spanDecimal);
  const baseDenominator = spanFraction.denominator;

  while (spanFraction.denominator > 8) {
    spanFraction = spanFraction.subtract(new Fraction(1, baseDenominator));
  }
  let startFraction = new Fraction(initialFraction[thm_flex]);

  let multFraction = spanFraction.subtract(startFraction);
  let multiplier = multFraction.numerator * (8 / multFraction.denominator);

  let pitch = "";
  if (multiplier < 16) {
    pitch = new Fraction(15, 16)
      .subtract(new Fraction(multiplier, 16))
      .toString();

    if (multiplier < 15) {
      return {
        thm_for: pitch,
        thm_rev: "",
      };
    } else {
      return {
        thm_for: "",
        thm_rev: "",
      };
    }
  } else {
    multiplier -= 15;
    pitch = new Fraction(multiplier, 16).toString();

    return {
      thm_rev: pitch,
      thm_for: "",
    };
  }
};

export const findInsertSize = (size, idx_sizes_data) => {
  const result = R.find((e) => {
    return e.size && size ? e.size.trim() === size.trim() : false;
  })(idx_sizes_data);

  return R.propOr("", "od", result);
};

export const getValue = (dcValue) => {
  if (typeof dcValue === "string") {
    return dcValue;
  }

  const cleanDcValue = JSON.parse(JSON.stringify(dcValue));

  return R.pipe(R.values, R.join(""))(cleanDcValue);
};

export const checkValidValue = (validValue) =>
  validValue === undefined || validValue === "";

export const isValid = (values) => {
  for (const k in values) {
    if (!checkValidValue(values[k])) {
      return false;
    }
  }

  return true;
};

export const toFraction = (decimal) => {
  return new Fraction(decimal).toString();
};

export const toClosestFraction = (fraction, spanMeasurement = "16+") => {
  const decimal = toDecimal(fraction, spanMeasurement);
  return new Fraction(Math.round(64 * decimal), 64).toString();
};

export const toDecimal = (fraction, spanMeasurement) => {
  var fractionToUse = fraction;
  if (fraction && fraction[0] === "-") {
    if (fraction[1] === " ") {
      fractionToUse = "-" + fraction.replace("- ", "");
    } else {
      fractionToUse = fraction.replace(" ", " -");
    }
  }
  let f = new Fraction(R.trim((fractionToUse || "").split("+")[0]));
  const isNegative = f.numerator < 0; // Check if the whole number is negative
  if ((fraction || "").indexOf("+") > -1) {
    if (spanMeasurement === "16+") {
      f = f.add(new Fraction(isNegative ? -1 : 1, 32)); // Adjust fraction sign
    }
    if (spanMeasurement === "32+") {
      f = f.add(new Fraction(isNegative ? -1 : 1, 64)); // Adjust fraction sign
    }
    if (spanMeasurement === "64+") {
      f = f.add(new Fraction(isNegative ? -1 : 1, 128)); // Adjust fraction sign
    }
  }

  return f.numerator / f.denominator;
};

export const toFractionRound = (decimal, round) => {
  return new Fraction(Math.round(round * decimal), round).toString();
};

export const cropImage = async (
  imageData,
  width,
  height,
  circleWidth,
  circle,
  x = 0,
  y = 0
) => {
  try {
    const croppiePhoto = document.createElement("div");
    croppiePhoto.style.position = "absolute";
    croppiePhoto.style.top = "-0px";
    croppiePhoto.style.left = "-0px";
    croppiePhoto.style.width = `${width}px`;
    croppiePhoto.style.height = `${height}px`;

    document.body.appendChild(croppiePhoto);

    const imgCroppie = new Croppie(croppiePhoto, {
      viewport: {
        width: circleWidth,
        height: circleWidth,
        type: circle ? "circle" : "square",
      },
      boundary: {
        width: width,
        height: height,
      },
      showZoomer: false,
      zoom: 0,
      objectFit: "contain",
    });

    await imgCroppie.bind({
      url: "data:image/png;base64," + imageData,
      zoom: 0,
      points: [x, y, x + width, y + height],
    });

    const croppedData = await imgCroppie.result("base64");

    const block = croppedData.split(";");
    const dataType = block[0].split(":")[1];
    const realData = block[1].split(",")[1];
    imgCroppie.destroy();
    croppiePhoto.parentNode.removeChild(croppiePhoto);
    return { realData, dataType };
  } catch (e) {
    throw e;
  }
};

export const getBase64Image = (
  imageData,
  width,
  height,
  circleWidth,
  circle,
  x = 0,
  y = 0,
  zoom = 1,
  scale = 1
) => {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement("canvas");
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        let offsetX = 0;
        let offsetY = 0;
        let imgScale = scale;
        if (circle) {
          imgScale = width / img.width;
          canvas.width = circleWidth;
          canvas.height = circleWidth;
          offsetX = (img.width - circleWidth) / 2;
          offsetY = (img.height - circleWidth) / 2;
        } else {
          canvas.width = img.width * imgScale;
          canvas.height = img.height * imgScale;
        }

        const ctx = canvas.getContext("2d");

        ctx.drawImage(
          img,
          0,
          0,
          img.width,
          img.height,
          x - offsetX - (img.width * (zoom * imgScale - 1)) / 2,
          y - offsetY - (img.height * (zoom * imgScale - 1)) / 2,
          img.width * zoom * imgScale,
          img.height * zoom * imgScale
        );

        if (circle) {
          ctx.fillStyle = "#fff";
          ctx.globalCompositeOperation = "destination-in";
          ctx.beginPath();
          ctx.arc(
            canvas.width / 2,
            canvas.height / 2,
            circleWidth / 2,
            0,
            Math.PI * 2,
            true
          );
          ctx.closePath();
          ctx.fill();
        }

        const dataURL = canvas.toDataURL("image/png");
        const block = dataURL.split(";");
        const dataType = block[0].split(":")[1];
        const realData = block[1].split(",")[1];

        resolve({ realData, dataType });
      };
      img.src = imageData;
    } catch (e) {
      reject(e);
    }
  });
};
